import { ref, set, push, update, remove } from 'firebase/database';
import { db } from '../config/firebase';
import { Expense, Settlement } from '../types/expense';
import { TripMember } from '../types/trip';
import { getDisplayMembers } from '../utils/members';

export async function addExpense(
  tripId: string,
  expense: Omit<Expense, 'expenseId' | 'createdAt'>
): Promise<string> {
  const expenseRef = push(ref(db, `trips/${tripId}/expenses`));
  const expenseId = expenseRef.key!;
  const fullExpense: Expense = {
    ...expense,
    expenseId,
    createdAt: Date.now(),
  };
  await set(expenseRef, fullExpense);
  return expenseId;
}

export async function updateExpense(
  tripId: string,
  expenseId: string,
  updates: Partial<Expense>
): Promise<void> {
  await update(ref(db, `trips/${tripId}/expenses/${expenseId}`), updates);
}

export async function deleteExpense(
  tripId: string,
  expenseId: string
): Promise<void> {
  await remove(ref(db, `trips/${tripId}/expenses/${expenseId}`));
}

/**
 * Calculate who owes whom after all expenses.
 *
 * Math model:
 *   For each splittable expense, the payer gets +amount (they fronted it),
 *   and each person in splitAmong gets -(amount / splitCount) (their share).
 *   After processing all expenses, each person's balance is (paid - owed).
 *   Positive balance = creditor (overpaid), negative = debtor (underpaid).
 *
 * Rules:
 *   - noSplit expenses are excluded entirely (personal costs, not group)
 *   - Anonymous/guest members are excluded from splits and settlements
 *   - Only current, non-anonymous members in splitAmong are counted
 *   - Payer is only credited if they're a current member
 *   - Amounts are coerced to Number() to guard against string values from Firebase
 *
 * Settlement algorithm (greedy):
 *   Sort creditors and debtors by amount descending. Match largest creditor
 *   with largest debtor, transfer the minimum of the two, repeat. This
 *   minimizes the number of transfers. Total debtor amounts always equals
 *   total creditor amounts (the system is zero-sum).
 */
export function calculateSettlements(
  expenses: Expense[],
  members: Record<string, TripMember>
): Settlement[] {
  // Step 1: Initialize balances for all members
  const balances: Record<string, number> = {};
  Object.keys(members).forEach(uid => (balances[uid] = 0));

  // Step 2: Filter to display members only (excludes stale anonymous guests)
  const memberUids = new Set(
    getDisplayMembers(members).map((m) => m.uid)
  );

  // Step 3: Process each expense — each expense counted exactly once
  expenses.forEach(expense => {
    // noSplit expenses don't affect balances (personal, not group)
    if (expense.noSplit) return;

    // Only include splitAmong UIDs that are current, non-anonymous members
    const splitAmong = (expense.splitAmong || []).filter(uid => memberUids.has(uid));
    const splitCount = splitAmong.length;
    if (splitCount === 0) return;

    const amt = Number(expense.amount);
    const share = amt / splitCount;

    // Credit the payer: they fronted the full amount
    if (memberUids.has(expense.paidByUid)) {
      balances[expense.paidByUid] += amt;
    }

    // Debit each person in the split: they owe their share
    splitAmong.forEach(uid => {
      balances[uid] -= share;
    });
  });

  // Step 4: Separate into creditors (positive balance) and debtors (negative)
  const creditors: [string, number][] = [];
  const debtors: [string, number][] = [];

  Object.entries(balances).forEach(([uid, bal]) => {
    const rounded = Math.round(bal * 100) / 100;
    if (rounded > 0) creditors.push([uid, rounded]);
    else if (rounded < 0) debtors.push([uid, Math.abs(rounded)]);
  });

  // Step 5: Greedy settlement — match largest creditor with largest debtor
  creditors.sort(([, a], [, b]) => b - a);
  debtors.sort(([, a], [, b]) => b - a);

  const settlements: Settlement[] = [];
  let ci = 0;
  let di = 0;

  while (ci < creditors.length && di < debtors.length) {
    const [creditorUid, creditAmt] = creditors[ci];
    const [debtorUid, debtAmt] = debtors[di];
    const transfer = Math.min(creditAmt, debtAmt);

    if (transfer > 0.01) {
      settlements.push({
        fromUid: debtorUid,
        toUid: creditorUid,
        amount: Math.round(transfer * 100) / 100,
        settled: false,
      });
    }

    creditors[ci] = [creditorUid, creditAmt - transfer];
    debtors[di] = [debtorUid, debtAmt - transfer];

    if (creditors[ci][1] < 0.01) ci++;
    if (debtors[di][1] < 0.01) di++;
  }

  return settlements;
}
